// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { getBundles } from '@/bundle/api/get-bundles';
import { Bundle } from '@/bundle/api/types';
import { bundlesQueryKey } from '@/bundle/hooks/use-bundles';
import { AddRounded } from '@/constants/hardfin-standard-icons';
import { pluralize } from '@/utilities/pluralize';
import {
  Autocomplete,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

/**
 * Special ID used to identify the "create new bundle" option in the dropdown.
 */
const CREATE_BUNDLE_OPTION_ID = 'create-bundle';

/**
 * Synthetic option appended to the results when the typed text does not
 * match an existing bundle name, offering to create a bundle by that name.
 */
interface CreateBundleOption {
  id: typeof CREATE_BUNDLE_OPTION_ID;
  name: string;
}

type BundleOption = Bundle | CreateBundleOption;

function isCreateBundleOption(
  option: BundleOption
): option is CreateBundleOption {
  return option.id === CREATE_BUNDLE_OPTION_ID;
}

/**
 * Orders picker options: non-archived bundles alphabetically first, then
 * archived bundles alphabetically at the bottom.
 */
function compareBundles(a: Bundle, b: Bundle): number {
  if (a.isArchived !== b.isArchived) {
    return a.isArchived ? 1 : -1;
  }
  return a.name.localeCompare(b.name);
}

/**
 * Opens the bundles page in a new tab with a "new" query parameter.
 * This triggers the "Add bundle" dialog to open with the bundle name
 * pre-populated.
 */
function openAddBundlePage(name: string) {
  const url = new URL('/catalog/bundles', document.location.origin);
  url.searchParams.append('new', encodeURIComponent(name));
  window.open(url, '_blank');
}

interface Props {
  /**
   * The currently selected bundle.
   */
  value: Bundle | null;

  /**
   * Callback fired when a bundle is selected or cleared.
   */
  onChange: (value: Bundle | null) => void;

  /**
   * The label text displayed above the input element.
   */
  label?: string;

  /**
   * If true, displays the component in an error state.
   */
  error?: boolean;

  /**
   * Text displayed below the input element.
   */
  helperText?: string;

  /**
   * If true, the input is disabled.
   */
  disabled?: boolean;
}

/**
 * The bundle picker used by the apply modal. With no typed text the
 * dropdown offers non-archived bundles only, alphabetically; typing
 * surfaces matching archived bundles at the bottom of the results with
 * the standard archived-option treatment. Items never appear here,
 * mirroring how bundles never appear in ItemSearch.
 *
 * When the typed text does not exactly match an existing bundle name,
 * an "Add bundle" option is appended at the bottom of the results;
 * selecting it opens the bundles page in a new tab with the create
 * dialog pre-populated with that name.
 */
export function BundleSearch(props: Props) {
  const { data, isPending } = useQuery({
    queryKey: [...bundlesQueryKey, 'picker'],
    queryFn: () => getBundles({ archived: true, limit: -1 }),
  });

  const bundles = [...(data?.results ?? [])].sort(compareBundles);

  const filterOptions = (
    options: BundleOption[],
    state: { inputValue: string }
  ): BundleOption[] => {
    const existingBundles = options.filter(
      (option): option is Bundle => !isCreateBundleOption(option)
    );
    const query = state.inputValue.trim();
    const loweredQuery = query.toLowerCase();

    if (query === '') {
      // Archived bundles are reachable by search but never offered by default
      return existingBundles.filter((option) => !option.isArchived);
    }

    const filtered: BundleOption[] = existingBundles.filter((option) =>
      option.name.toLowerCase().includes(loweredQuery)
    );

    // Offer creating a bundle when the typed name has no exact match
    const hasExactMatch = existingBundles.some(
      (option) => option.name.toLowerCase() === loweredQuery
    );
    if (!hasExactMatch) {
      filtered.push({ id: CREATE_BUNDLE_OPTION_ID, name: query });
    }

    return filtered;
  };

  /**
   * Handles changes to the selected value, including:
   * - Direct bundle selection
   * - Creation of new bundles
   * - Clearing the selection
   */
  const handleChange = (_: unknown, value: BundleOption | string | null) => {
    if (typeof value === 'string') {
      return openAddBundlePage(value);
    }

    if (value === null) {
      return props.onChange(null);
    }

    if (isCreateBundleOption(value)) {
      openAddBundlePage(value.name);
      return;
    }

    props.onChange(value);
  };

  return (
    <Autocomplete
      fullWidth
      freeSolo
      clearOnBlur
      forcePopupIcon={true}
      size="small"
      options={bundles}
      value={props.value}
      onChange={handleChange}
      filterOptions={filterOptions}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.name
      }
      isOptionEqualToValue={(option, value) => option.id === value.id}
      loading={isPending}
      disabled={props.disabled}
      renderOption={(optionProps, option) => {
        if (isCreateBundleOption(option)) {
          return (
            <li {...optionProps} key={option.id}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AddRounded fontSize="small" color="primary" />
                <Typography color="primary.main">
                  Add bundle <b>{option.name}</b>
                </Typography>
              </Stack>
            </li>
          );
        }

        return (
          <li {...optionProps} key={option.id}>
            <Typography
              color={option.isArchived ? 'text.secondary' : 'text.primary'}
            >
              {option.name} {option.isArchived && '(Archived)'}
              <Typography component="span" color="text.secondary">
                {' '}
                ({pluralize(option.members.length, { singular: 'item' })})
              </Typography>
            </Typography>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={props.label ?? 'Bundle'}
          error={props.error}
          helperText={props.helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isPending && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
